import React, { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  TextField,
  Button,
  CircularProgress,
  SvgIcon,
  alpha,
  useTheme,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  OpenInNewRounded,
  DeleteOutlineRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Standard TikTok SVG icon
const TikTokIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z"
    />
  </SvgIcon>
);

export const SOCIAL_PLATFORMS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    brandColor: '#0A66C2',
    icon: LinkedInIcon,
    placeholder: 'https://www.linkedin.com/in/nom-utilisateur',
    helpExample: 'https://www.linkedin.com/in/...',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    brandColor: '#1877F2',
    icon: FacebookIcon,
    placeholder: 'https://www.facebook.com/nom-utilisateur',
    helpExample: 'https://www.facebook.com/...',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    brandColor: '#E4405F',
    icon: InstagramIcon,
    placeholder: 'https://www.instagram.com/nom ou @nom',
    helpExample: 'https://www.instagram.com/... ou @utilisateur',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    brandColor: '#FE2C55',
    darkBrandColor: '#FE2C55',
    lightBrandColor: '#010101',
    icon: TikTokIcon,
    placeholder: 'https://www.tiktok.com/@nom ou @nom',
    helpExample: 'https://www.tiktok.com/@... ou @utilisateur',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    brandColor: '#FF0000',
    icon: YouTubeIcon,
    placeholder: 'https://www.youtube.com/@chaine',
    helpExample: 'https://www.youtube.com/@...',
  },
];

/**
 * Validate and normalize a social link according to the platform.
 */
export function normalizeAndValidateSocialUrl(networkId, rawInput, lang = 'fr') {
  if (!rawInput || !String(rawInput).trim()) {
    return { ok: true, normalized: '', error: null };
  }

  let value = String(rawInput).trim();

  // Normalize @handle shortcuts for Instagram & TikTok
  if (networkId === 'instagram' && value.startsWith('@')) {
    value = `https://www.instagram.com/${value.slice(1)}`;
  } else if (networkId === 'tiktok' && value.startsWith('@')) {
    value = `https://www.tiktok.com/@${value.slice(1)}`;
  }

  // Prepend https:// if protocol is missing
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value}`;
  }

  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();

    if (networkId === 'linkedin') {
      if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) {
        return {
          ok: false,
          error:
            lang === 'fr'
              ? "L'adresse doit être un lien LinkedIn valide (ex: https://www.linkedin.com/in/...)"
              : 'Must be a valid LinkedIn URL (e.g. https://www.linkedin.com/in/...)',
        };
      }
    } else if (networkId === 'facebook') {
      if (
        host !== 'facebook.com' &&
        !host.endsWith('.facebook.com') &&
        host !== 'fb.com' &&
        !host.endsWith('.fb.com')
      ) {
        return {
          ok: false,
          error:
            lang === 'fr'
              ? "L'adresse doit être un lien Facebook valide (ex: https://www.facebook.com/...)"
              : 'Must be a valid Facebook URL',
        };
      }
    } else if (networkId === 'instagram') {
      if (host !== 'instagram.com' && !host.endsWith('.instagram.com')) {
        return {
          ok: false,
          error:
            lang === 'fr'
              ? "L'adresse doit être un lien Instagram valide (ex: https://www.instagram.com/...)"
              : 'Must be a valid Instagram URL',
        };
      }
    } else if (networkId === 'tiktok') {
      if (host !== 'tiktok.com' && !host.endsWith('.tiktok.com')) {
        return {
          ok: false,
          error:
            lang === 'fr'
              ? "L'adresse doit être un lien TikTok valide (ex: https://www.tiktok.com/@...)"
              : 'Must be a valid TikTok URL',
        };
      }
    } else if (networkId === 'youtube') {
      if (
        host !== 'youtube.com' &&
        !host.endsWith('.youtube.com') &&
        host !== 'youtu.be' &&
        !host.endsWith('.youtu.be')
      ) {
        return {
          ok: false,
          error:
            lang === 'fr'
              ? "L'adresse doit être un lien YouTube valide (ex: https://www.youtube.com/@...)"
              : 'Must be a valid YouTube URL',
        };
      }
    }

    return { ok: true, normalized: parsed.toString(), error: null };
  } catch (err) {
    return {
      ok: false,
      error: lang === 'fr' ? 'Format URL non valide.' : 'Invalid URL format.',
    };
  }
}

/**
 * SocialLinksEditor
 *
 * Displays social network icons side by side:
 * - Grey/muted when empty
 * - Full brand color when added
 * - Clicking opens popover to edit URL, validate in real time, and save
 */
const SocialLinksEditor = ({
  values = {},
  onSave,
  size = 'medium',
  disabled = false,
  extraNotice,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [activePlatform, setActivePlatform] = useState(null);
  const [draftUrl, setDraftUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const buttonDimensions = useMemo(() => {
    switch (size) {
      case 'small':
        return { boxSize: 30, iconSize: 17, radius: 1.5 };
      case 'large':
        return { boxSize: 42, iconSize: 24, radius: 2.2 };
      default:
        return { boxSize: 35, iconSize: 20, radius: 2 };
    }
  }, [size]);

  const handleOpenPopover = (event, platform) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setActivePlatform(platform);
    const existingVal = values?.[platform.id] || '';
    setDraftUrl(existingVal);
  };

  const handleClosePopover = () => {
    if (isSaving) return;
    setAnchorEl(null);
    setActivePlatform(null);
    setDraftUrl('');
  };

  const validation = useMemo(() => {
    if (!activePlatform) return { ok: true, normalized: '', error: null };
    return normalizeAndValidateSocialUrl(activePlatform.id, draftUrl, lang);
  }, [activePlatform, draftUrl, lang]);

  const handleSave = async (valueToSave = null) => {
    if (!activePlatform || !onSave) return;
    const finalVal = valueToSave !== null ? valueToSave : validation.normalized;

    setIsSaving(true);
    try {
      await onSave(activePlatform.id, finalVal);
      handleClosePopover();
    } catch (err) {
      console.error('[SocialLinksEditor] Error saving social link:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    handleSave('');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {SOCIAL_PLATFORMS.map((platform) => {
        const IconComponent = platform.icon;
        const currentUrl = values?.[platform.id] || '';
        const isSet = Boolean(currentUrl && String(currentUrl).trim());

        const brandColor =
          platform.id === 'tiktok'
            ? theme.palette.mode === 'dark'
              ? platform.darkBrandColor
              : platform.lightBrandColor
            : platform.brandColor;

        const tooltipTitle = isSet
          ? `${platform.name}: ${currentUrl}`
          : t('addSocialNetwork', {
              network: platform.name,
              defaultValue: `Ajouter ${platform.name}`,
            });

        return (
          <Tooltip key={platform.id} title={tooltipTitle} arrow placement="top">
            <IconButton
              size="small"
              disabled={disabled}
              onClick={(e) => handleOpenPopover(e, platform)}
              aria-label={platform.name}
              sx={{
                width: buttonDimensions.boxSize,
                height: buttonDimensions.boxSize,
                borderRadius: buttonDimensions.radius,
                border: isSet
                  ? `1.5px solid ${alpha(brandColor, 0.45)}`
                  : `1px dashed ${theme.palette.divider}`,
                backgroundColor: isSet
                  ? alpha(brandColor, theme.palette.mode === 'dark' ? 0.2 : 0.08)
                  : theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.03)',
                color: isSet ? brandColor : theme.palette.text.disabled,
                transition: 'all 160ms ease',
                '&:hover': {
                  backgroundColor: isSet
                    ? alpha(brandColor, theme.palette.mode === 'dark' ? 0.32 : 0.16)
                    : alpha(theme.palette.text.primary, 0.08),
                  borderColor: isSet ? brandColor : theme.palette.text.secondary,
                  color: isSet ? brandColor : theme.palette.text.primary,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <IconComponent sx={{ fontSize: buttonDimensions.iconSize }} />
            </IconButton>
          </Tooltip>
        );
      })}

      {/* Popover to enter / edit URL */}
      <Popover
        open={Boolean(anchorEl && activePlatform)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 330,
            maxWidth: '92vw',
            borderRadius: 2.5,
            boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {activePlatform && (
          <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: alpha(
                      activePlatform.brandColor,
                      theme.palette.mode === 'dark' ? 0.25 : 0.12
                    ),
                    color:
                      activePlatform.id === 'tiktok' && theme.palette.mode === 'light'
                        ? '#000'
                        : activePlatform.brandColor,
                  }}
                >
                  {React.createElement(activePlatform.icon, { sx: { fontSize: 17 } })}
                </Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {activePlatform.name}
                </Typography>
              </Box>

              {values?.[activePlatform.id] && (
                <Tooltip
                  title={t('openInNewTab', { defaultValue: 'Ouvrir dans un nouvel onglet' })}
                  arrow
                >
                  <IconButton
                    size="small"
                    component="a"
                    href={values[activePlatform.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'text.secondary', p: 0.5 }}
                  >
                    <OpenInNewRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Input */}
            <TextField
              autoFocus
              size="small"
              fullWidth
              value={draftUrl}
              placeholder={activePlatform.placeholder}
              error={Boolean(draftUrl.trim() && !validation.ok)}
              helperText={
                draftUrl.trim() && !validation.ok
                  ? validation.error
                  : activePlatform.helpExample
              }
              onChange={(e) => setDraftUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && validation.ok && !isSaving) {
                  e.preventDefault();
                  handleSave();
                }
              }}
              sx={{
                mt: 0.5,
                '& .MuiInputBase-root': {
                  fontSize: 12.5,
                  borderRadius: 1.5,
                },
                '& .MuiFormHelperText-root': {
                  mx: 0,
                  mt: 0.5,
                  fontSize: 10.5,
                  lineHeight: 1.25,
                },
              }}
            />

            {/* LinkedIn specific helpful notice */}
            {activePlatform.id === 'linkedin' && (
              <Box
                sx={{
                  mt: 1.25,
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: alpha('#0A66C2', theme.palette.mode === 'dark' ? 0.15 : 0.06),
                  border: `1px solid ${alpha('#0A66C2', 0.25)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <CheckCircleRounded sx={{ fontSize: 14, color: '#0A66C2', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.3 }}>
                  {t('linkedinSyncNotice', {
                    defaultValue:
                      'Permet de générer automatiquement un avatar IA et de pré-remplir le titre et téléphone.',
                  })}
                </Typography>
              </Box>
            )}

            {extraNotice && <Box sx={{ mt: 1 }}>{extraNotice}</Box>}

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 2,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {values?.[activePlatform.id] ? (
                <Button
                  size="small"
                  color="error"
                  onClick={handleClear}
                  disabled={isSaving}
                  startIcon={<DeleteOutlineRounded sx={{ fontSize: 15 }} />}
                  sx={{ textTransform: 'none', fontSize: 11.5, px: 1 }}
                >
                  {t('remove', { defaultValue: 'Effacer' })}
                </Button>
              ) : (
                <Box />
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleClosePopover}
                  disabled={isSaving}
                  sx={{ textTransform: 'none', fontSize: 11.5 }}
                >
                  {t('cancel', { defaultValue: 'Annuler' })}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSave()}
                  disabled={!validation.ok || isSaving}
                  sx={{ textTransform: 'none', fontSize: 11.5, minWidth: 80 }}
                >
                  {isSaving ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    t('save', { defaultValue: 'Enregistrer' })
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default SocialLinksEditor;
